"use client";

import { useQuery } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import type { MedicationOption } from "@rmh/defs";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

/** Canvas's `/Medication` endpoint needs a non-trivial query; mirror its floor. */
export const MIN_MEDICATION_QUERY_LENGTH = 2;

interface SearchMedicationsQueryVariables {
  text: string;
}

interface SearchMedicationsQueryData {
  searchMedications: MedicationOption[];
}

export const searchMedicationsQuery = gql`
  query searchMedications($text: String!) {
    searchMedications(text: $text) {
      fdbCode
      rxnormCode
      display
    }
  }
`.loc?.source.body as GeneratedQuery<
  SearchMedicationsQueryVariables,
  SearchMedicationsQueryData
>;

interface UseSearchMedicationsOptions {
  /** The (already debounced) search text from the autocomplete input. */
  text: string;
  enabled?: boolean;
}

/**
 * Typeahead over FDB medication catalog; each option carries the FDB coding for
 * a structured MedicationStatement. Pass debounced text; idle until min length.
 */
export const useSearchMedications = ({
  text,
  enabled = true,
}: UseSearchMedicationsOptions) => {
  const trimmed = text.trim();
  return useQuery<MedicationOption[]>({
    queryKey: ["medications", trimmed],
    queryFn: async () => {
      const response = await fetchGraphQLClient(searchMedicationsQuery, {
        text: trimmed,
      });
      return response.data?.searchMedications ?? [];
    },
    enabled: enabled && trimmed.length >= MIN_MEDICATION_QUERY_LENGTH,
    // The FDB catalog is effectively static for a session — don't refetch.
    staleTime: 5 * 60 * 1000,
  });
};
